import React, { useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import {
  MonitoringActiveItemCard,
  MonitoringActiveItemGrid,
  formatMonitoringActiveSince,
} from './MonitoringActiveItemCard';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { apiService } from '../../services/api';
import { LoadingSpinner } from '../LoadingSpinner';
import { MonitoringActivationLogList } from '../MonitoringActivationLogList';
import { ChangeEmailDeliveryLogList } from '../ChangeEmailDeliveryLogList';

type Props = {
  enabled?: boolean;
};

export function AgenciesMonitoringPanel({ enabled = true }: Props) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);

  const { data, isLoading, refetch } = useQuery(
    ['agencies-monitoring-history'],
    () => apiService.getAgenciesMonitoringHistory(),
    { enabled, refetchOnWindowFocus: false },
  );

  const { data: emailLogs, isLoading: emailLoading } = useQuery(
    ['agencies-monitoring-delivery-logs'],
    () => apiService.getAgenciesMonitoringDeliveryLogs(),
    { enabled, refetchOnWindowFocus: false },
  );

  const toggleMutation = useMutation(
    (agencyCode: string) => apiService.toggleAgencyMonitoring(agencyCode),
    {
      onSuccess: (result) => {
        toast.success(result.message);
        void refetch();
        queryClient.invalidateQueries(['agencies-monitoring']);
        queryClient.invalidateQueries(['agencies-monitoring-history']);
        queryClient.invalidateQueries(['dashboard-changes']);
      },
      onError: () => toast.error('Erro ao alterar monitoramento da agência'),
    },
  );

  const agencies = data?.agencies ?? [];
  const history = data?.history ?? [];
  const emails = emailLogs ?? [];
  const activeCount = agencies.filter((a) => a.is_monitored).length;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <LoadingSpinner message="Carregando agências..." />
      </Box>
    );
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {activeCount > 0
          ? `${activeCount} agência(s) ativa(s) gerando notificações (e-mail D+1 e sininho).`
          : 'Nenhuma agência ativa. Ative no Dashboard → Monitorar Agências.'}
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          aria-label="Abas do histórico de agências monitoradas"
        >
          <Tab label={`Agências monitoradas (${activeCount})`} />
          <Tab label={`Logs de ativação (${history.length})`} />
          <Tab label={`E-mails (${emails.length})`} />
        </Tabs>
      </Box>

      {activeTab === 0 &&
        (agencies.length > 0 ? (
          <MonitoringActiveItemGrid>
            {agencies.map((item) => (
              <MonitoringActiveItemCard
                key={item.agency_code}
                title={item.agency_name}
                subtitle={
                  item.is_monitored
                    ? formatMonitoringActiveSince(item.last_event?.timestamp ?? item.activated_since)
                    : item.last_event
                      ? `Último evento em ${formatMonitoringActiveSince(item.last_event.timestamp)}`
                      : 'Sem eventos'
                }
                isActive={item.is_monitored}
                badge={item.agency_code.toUpperCase()}
                onToggle={() => toggleMutation.mutate(item.agency_code)}
                toggleLoading={toggleMutation.isLoading}
              />
            ))}
          </MonitoringActiveItemGrid>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">Nenhuma agência monitorada.</Typography>
          </Box>
        ))}

      {activeTab === 1 && <MonitoringActivationLogList history={history} />}

      {activeTab === 2 && <ChangeEmailDeliveryLogList logs={emails} loading={emailLoading} />}
    </>
  );
}
